import { ResourceTypes } from "model/party/resources/resources";
import { HistoryTypes } from "model/party/history/history";
import Story from "model/events/story";
import strangerEnd from "model/events/story/strangerEnd";
import * as City from "model/events/city";
import mountainEvents from "model/events/terrain/mountain";
import hillEvents from "model/events/terrain/hill";
import riverEvents from "model/events/terrain/river";
import plainsEvents from "model/events/terrain/plains";
import waterEvents from "model/events/terrain/water";
import { borderEvents, randomEvents, specialEvents } from "model/events/road";

import combatEvent from "model/events/combat";
import * as Stuck from "model/events/stuck";
import death from "model/events/death";
import WalkEvent from "model/events/walk";

import { CityActionTypes } from "model/actions";
import { WalkType } from "model/actions/walk";
import die from "model/actions/health/die";
import createAction from "model/actions/utilities/createAction";
import nextAction from "model/actions/utilities/nextAction";
import Debug from "debug";
const debug = Debug("controllers:EventManager");

const DAYS_ELAPSED_MEDICINE_WOMAN = 2;
const DAYS_ELAPSED_ROB = 3;
const DAYS_ELAPSED_RANCHER_WORK = 5;

/**
 * The town's arrivals, in the order they come.
 *
 * These used to be three `DAYS_ELAPSED_X <= daysElapsed && !flag` blocks, which re-offered
 * the same beat on every day change until the player clicked the button that set the flag.
 * That was survivable while days only moved when the player clicked; now that the clock
 * owns days in town, it would put the healer on screen every day forever.
 *
 * `offered` is the fix rather than switching to `===`: a strict day match would drop the
 * beat entirely for a player who is on the road that day, and these are written as town
 * content — hence the `party.city` gate too.
 */
const TOWN_ONE_SHOTS = [
  { day: DAYS_ELAPSED_MEDICINE_WOMAN, flag: "medicineWoman", event: City.medicineWoman },
  { day: DAYS_ELAPSED_ROB, flag: "townDrunk", event: City.firstDrunk },
  { day: DAYS_ELAPSED_RANCHER_WORK, flag: "rancherWork", event: City.rancher },
];

const offered = new Set();

/**
 * The priest turning up dead, a couple of days after the beg branch leaves him in the dirt.
 *
 * It hangs off the day counter rather than off a story state because the machine's next stop
 * after StrangerBeg is the brothers, which is a town and the better part of a week away — there
 * is nowhere in that graph to put a beat that belongs to the town he is standing in now. It was
 * wired this way once (`DAYS_ELAPSED_STRANGER_END` in the pre-xstate Events.js) and the restructure
 * dropped it; strangerEnd.js has been unreachable since.
 *
 * Armed on the first day change after the beg branch closes, and it remembers *which* town — a
 * body found in the shallows is that town's news, and the traveler who has already ridden on
 * simply never hears it. So a player who leaves early loses the beat, which is right, and it can
 * never surface a first-town death in the second town, which is the failure worth preventing.
 */
const DAYS_ELAPSED_STRANGER_END = 2;

let strangerEndBeat = null;

const RANDOM_EVENT_CHANCE = 0.3;
const RANDOM_EVENT_CHANCE_RIVER = 0.8;

function getEvents(context) {
  const { party, terrain } = context;
  if (!party.canContinue()) {
    if (party.hasCoins()) {
      return [Stuck.lifeline];
    }
    return [Stuck.rescue];
  }

  debug(terrain.context);
}

function triggerRandomEvent(context, setEvent) {
  const { calendar, party, terrain } = context;

  const timeOfDay = calendar.isNight() ? "night" : "day";
  const season = calendar.season().toLowerCase();

  const eventTriggered =
    Math.random() <
      (terrain.context.inRiver
        ? RANDOM_EVENT_CHANCE_RIVER
        : RANDOM_EVENT_CHANCE);
  if (!eventTriggered) {
    return null;
  }

  // Borders
  if (party.crossedBorder) {
    const event = triggerWeightedEvent(
      borderEvents,
      season,
      timeOfDay,
      context,
      setEvent,
    );
    return event;
  }

  let event = null;
  if (terrain.context.inRiver) {
    event = triggerWeightedEvent(
      riverEvents,
      season,
      timeOfDay,
      context,
      setEvent,
    );
  } else if (terrain.context.inWater) {
    // The middle of the sea
    event = triggerWeightedEvent(
      waterEvents,
      season,
      timeOfDay,
      context,
      setEvent,
    );
  } else if (Math.random() < 0.2) {
    event = triggerWeightedEvent(
      randomEvents,
      season,
      timeOfDay,
      context,
      setEvent,
    );
  } else if (terrain.context.inMountain) {
    event = triggerWeightedEvent(
      mountainEvents,
      season,
      timeOfDay,
      context,
      setEvent,
    );
  } else if (terrain.context.onHill) {
    event = triggerWeightedEvent(
      hillEvents,
      season,
      timeOfDay,
      context,
      setEvent,
    );
  } else if (terrain.context.onPlain) {
    event = triggerWeightedEvent(
      plainsEvents,
      season,
      timeOfDay,
      context,
      setEvent,
    );
  }

  return event;
}

const story = Story();

const onLevelChanged = (path, newLevel, context, setEvent) => {
  const nextEvents = story.nextEvents();
  let event = null;
  let storyEventType = path.label === "Labour"
    ? "WORK"
    : path.label === "Charity"
    ? "BEG"
    : path.label === "Crime"
    ? "ROB"
    : null;

  const triggeredEvent = nextEvents.find((event) =>
    event.type === storyEventType
  );

  let onLevelUp = newLevel.onLevelUp;
  if (onLevelUp) {
    if (typeof onLevelUp === "function") {
      onLevelUp = onLevelUp(context, setEvent);
    }

    // The event itself is the variant. Every other path into createActionsFromButtons hands it
    // one — getEventVariant passes `variant[timeOfDay]` for events that have variants and the
    // event itself for those that don't — and `mergeEffects(variant?.effect, …)` is what folds
    // the event-level `effect` into each button. Passing `null` here meant no level-up event's
    // own effect had ever been applied on any of the four skill paths: only the accept button's
    // half of the grant landed. Crime's ladder, for one, was paying 9 infamy across eleven
    // rungs where the file reads 21.
    onLevelUp = {
      ...onLevelUp,
      buttons: createActionsFromButtons(
        onLevelUp.buttons,
        onLevelUp.title,
        onLevelUp,
        context,
        setEvent,
      ),
    };
  }

  event = triggeredEvent
    ? story.next(setEvent, context, triggeredEvent)
    : onLevelUp;

  debug("On Level Change", path, newLevel, storyEventType, event, context);
  setEvent(event);
};

const triggerStoryEvent = (daysElapsed, context, onDone) => {
  const {
    party: { history },
  } = context;

  // Read before the machine is given a chance to move off StrangerBeg below.
  if (!strangerEndBeat && story.state() === "StrangerBeg" && context.party.city) {
    strangerEndBeat = {
      day: daysElapsed + DAYS_ELAPSED_STRANGER_END,
      city: context.party.city,
    };
  }

  const nextEvents = story.nextEvents();
  const triggeredEvent = nextEvents.find((event) =>
    event.isTriggered(daysElapsed, context)
  );
  debug(
    "daysElapsed",
    daysElapsed,
    "triggered: ",
    triggeredEvent,
    story.currentState,
    story.nextEvents(),
  );
  if (triggeredEvent) {
    const next = story.next(onDone, context, triggeredEvent);
    return next;
  }

  // Only in a town, and at most one arrival per day.
  if (!context.party.city) return;

  // Ahead of the arrivals below: it is a story beat with a day of its own, and losing it to a
  // one-per-day collision with the rancher would push it a day further from the body.
  if (
    strangerEndBeat &&
    !offered.has("strangerEnd") &&
    daysElapsed >= strangerEndBeat.day &&
    context.party.city === strangerEndBeat.city
  ) {
    debug("stranger end", "day", daysElapsed);
    offered.add("strangerEnd");
    return strangerEnd(onDone);
  }

  const due = TOWN_ONE_SHOTS.find(
    (beat) =>
      beat.day <= daysElapsed &&
      !offered.has(beat.flag) &&
      !history.attribs[beat.flag].value,
  );
  if (!due) return;

  debug("town one-shot", due.flag, "day", daysElapsed);
  offered.add(due.flag);
  return due.event(onDone);
};

const triggerSpecialEvent = (context, setEvent) => {
  if (Math.random() < 0.9) return null;

  const { calendar } = context;
  const timeOfDay = calendar.isNight() ? "night" : "day";
  const season = calendar.season().toLowerCase();

  let event = triggerWeightedEvent(
    specialEvents,
    season,
    timeOfDay,
    context,
    setEvent,
  );

  return event;
};

const onDayChange = (daysElapsed, context, setEvent) => {
  let event = triggerStoryEvent(daysElapsed, context, setEvent);
  if (event) {
    return setEvent(event);
  }
};

const onResourceChanged = (index, oldValue, newValue, setEvent, context) => {
  debug("onResourceChanged", index, oldValue, newValue);

  if (index === ResourceTypes.Health) {
    // Only on the crossing to zero. Without the oldValue guard every later health delta
    // re-runs die.execute and queues another death modal.
    if (oldValue > 0 && newValue <= 0) {
      die.execute(context);
    }
  }
};

/**
 * The law, on the open road.
 *
 * The counterpart to maybeTriggerPathEvent, which only ever fired on a robbery and so put the
 * posse in the one place the player had chosen to stand. A man who robbed a town and walked
 * out of it was safe the moment he left, in a game whose whole subject is walking. This is the
 * chase: rolled once per step for as long as party.isPursued(), and shaken by crossing out of
 * the region — see Party.setPoliticalContext.
 *
 * Note where this sits in the step. depart.js writes its CONTINUE_TRIP history entry *before*
 * calling takeStep, so the roll happens against the party's position as it leaves rather than
 * as it arrives. That is the right way round: the step that carries the party over a border is
 * still a step taken on the wrong side of it, and it should cost one last roll.
 */
const triggerPursuitEvent = (context, setEvent) => {
  const { party, calendar } = context;
  if (!party.isPursued()) return null;

  const pursuit = party.progression
    .getPath("crime")
    .pursuitEvent(context, setEvent);
  if (!pursuit) return null;

  const encounter = pursuit(context, setEvent);
  if (!encounter) return null;

  const timeOfDay = calendar.isNight() ? "night" : "day";
  const season = calendar.season().toLowerCase();
  // getEventVariant takes the head of the matching variants and can come back undefined if a
  // season falls through the filter. Null it here rather than handing a falsy event to
  // setEvent, which would dismiss the screen instead of filling it.
  return getEventVariant(encounter, season, timeOfDay, context, setEvent) ||
    null;
};

const maybeTriggerTravelEvent = (context, setEvent) => {
  if (context.party.city) return;

  // The law outranks the country. A posse closing is not something the weather gets to talk
  // over, so this is asked before the terrain is.
  let event = triggerPursuitEvent(context, setEvent);
  if (event) {
    return setEvent(event);
  }

  // Set random event
  event = triggerRandomEvent(context, setEvent);
  if (event) {
    return setEvent(event);
  }

  // Caravan event
  event = triggerSpecialEvent(context, setEvent);
  return setEvent(event);
};

const onHistoryActionsChanged = (
  action,
  total,
  successes,
  failures,
  setEvent,
  context,
) => {
  switch (action) {
    case CityActionTypes.ExamineMap:
      if (total === 1) setEvent(City.examineMap(context, setEvent));
      break;
    case CityActionTypes.TradePost:
      setEvent(City.tradePost(context, setEvent));
      break;
    case HistoryTypes.Death:
      // die.execute already called party.restore(), so party.step is the restored position.
      setEvent(death(setEvent, context?.party?.step ?? 0));
      break;
    case HistoryTypes.Rob:
      maybeTriggerPathEvent(context, setEvent);
      break;
    case HistoryTypes.ContinueTrip:
      maybeTriggerTravelEvent(context, setEvent);
      break;
    default:
      // Handle the default case
      debug(`Unhandled action: ${action}`);
  }

  debug(action, "total", total, "ok", successes, "fail", failures, context);
};

const maybeTriggerPathEvent = (context, setEvent) => {
  const { party, calendar } = context;
  const { progression } = party;

  // Assuming a 30% chance for a level-related event to occur
  if (Math.random() >= 0.3) {
    return;
  }

  const path = progression.getPath("crime");
  const rob = path.randomEvent(context);
  if (!rob) {
    debug("No rob event found");
    return setEvent(null);
  }
  const robEvent = rob(context, setEvent);

  const timeOfDay = calendar.isNight() ? "night" : "day";
  const season = calendar.season().toLowerCase();
  const robVariant = getEventVariant(
    robEvent,
    season,
    timeOfDay,
    context,
    setEvent,
  );
  setEvent(robVariant);
};

const createActionsFromButtons = (
  buttons,
  eventTitle,
  variant,
  context,
  setEvent,
) =>
  buttons.map((button) => {
    if (button.enabled !== undefined) return button;

    if (button.combat) {
      const { enemies, description, victory, defeat } = button.combat;
      const mergedEnemies = mergeEnemies(enemies, variant?.enemies);
      if (mergedEnemies.length !== 0) {
        return nextAction(button.title, () =>
          setEvent(
            combatEvent(
              eventTitle,
              description,
              mergedEnemies,
              victory,
              defeat,
              context,
              setEvent,
            ),
          ));
      }
    }

    return createAction(
      button.title,
      button.message,
      mergeEffects(variant?.effect, button.effect, button.isDismiss, context),
    )(context, setEvent);
  });

const getEventVariant = (event, season, timeOfDay, context, setEvent) => {
  if (event.variants) {
    return event.variants
      .filter((variant) => {
        const matchesSeason = variant.season === undefined ||
          variant.season === season ||
          (typeof variant.season === Array && variant.season.includes(season));
        const matchesTimeOfDay = variant[timeOfDay] !== undefined;
        return matchesSeason && matchesTimeOfDay;
      })
      .map((variant) => {
        const buttons = (variant[timeOfDay].buttons || []).concat(
          event.buttons || [],
        );
        return {
          ...event,
          description: variant[timeOfDay].description,
          effect: variant[timeOfDay].effect,
          weight: (event.baseWeight || 0) + (variant.weightAdjustment || 0),
          buttons: createActionsFromButtons(
            buttons,
            event.title,
            variant[timeOfDay],
            context,
            setEvent,
          ),
        };
      })[0];
  } else {
    return {
      ...event,
      buttons: createActionsFromButtons(
        event.buttons,
        event.title,
        event,
        context,
        setEvent,
      ),
    };
  }
};

const filterEvents = (events, season, timeOfDay, context, setEvent) => {
  return events.flatMap((event) => {
    if (event.condition && !event.condition(context)) {
      //  || !event.isCombat) {
      return [];
    }

    // If event variants exist, filter them based on the current season and timeOfDay
    return getEventVariant(event, season, timeOfDay, context, setEvent);
  });
};

const mergeEnemies = (buttonEnemies, varEnemies, context) => {
  let baseEnemies = buttonEnemies || [];
  if (typeof buttonEnemies === "function") {
    baseEnemies = buttonEnemies(context);
  }
  const merged = [...baseEnemies];

  let variantEnemies = varEnemies;
  if (typeof varEnemies === "function") {
    variantEnemies = varEnemies(context);
  }

  if (!variantEnemies) return merged;

  return merged.concat(variantEnemies);
};

const mergeEffects = (varEffect, buttonEffect, isDismiss, context) => {
  if (isDismiss) {
    // Dismissing the event will not trigger any effect
    return {};
  }

  let baseEffect = buttonEffect || {};
  if (typeof buttonEffect === "function") {
    baseEffect = buttonEffect(context);
  }
  const merged = { ...baseEffect };

  let variantEffect = varEffect;
  if (typeof varEffect === "function") {
    variantEffect = varEffect(context);
  }

  if (!variantEffect) return merged;

  Object.keys(variantEffect).forEach((key) => {
    if (!merged[key]) {
      // If the key does not exist in merged, simply copy it
      merged[key] = variantEffect[key];
    } else {
      // If the key exists, iterate through its nested keys for merging
      Object.keys(variantEffect[key]).forEach((nestedKey) => {
        if (merged[key].hasOwnProperty(nestedKey)) {
          // Sum the values if the nested key exists
          merged[key][nestedKey] += variantEffect[key][nestedKey];
        } else {
          // Otherwise, just copy the nested key-value pair
          merged[key][nestedKey] = variantEffect[key][nestedKey];
        }
      });
    }
  });

  return merged;
};

// season: "spring", "summer", "fall", "winter"
// timeOfDay: "day", "night"
function triggerWeightedEvent(eventFn, season, timeOfDay, context, setEvent) {
  // Filter events based on season and time of day, adjusting weights as necessary
  const events = eventFn(context, setEvent);
  const filteredEvents = filterEvents(
    events,
    season,
    timeOfDay,
    context,
    setEvent,
  ).filter((event) => event);

  // Calculate total weight for filtered events
  const totalWeight = filteredEvents.reduce(
    (sum, event) => sum + (event.weight || 0),
    0,
  );
  let randomNum = Math.floor(Math.random() * totalWeight);
  let accumulatedWeight = 0;

  for (const event of filteredEvents) {
    accumulatedWeight += event.weight || 0;
    if (randomNum < accumulatedWeight) {
      debug("triggerWeightedEvent", event);
      return event;
    }
  }
}

const onHistoryChanged = (index, oldValue, newValue, setEvent, context) => {
  debug("onHistoryChanged", index, oldValue, newValue);
  if (index === HistoryTypes.FirstTown && newValue) {
    const story = Story(setEvent);
    setEvent(story.enter);
  }
  // The map no longer opens the trade post on its own. It puts Examine Map in the town, and the
  // barkeep at the end of that scene is what opens the shop — see leaveBarkeep in
  // model/events/city. Her line in the trade post event is still written to the map, because by
  // the time she speaks the player has read one.
};

const trigger = (eventType, result, context, setEvent) => {
  debug("trigger", eventType);
  switch (eventType) {
    case WalkType:
      const event = WalkEvent.random(result, context, setEvent);
      return setEvent(event(setEvent));
    default:
      debug("Unhandled event", eventType);
  }
};

const resetEvents = () => {
  story.reset();
  // A new run re-arms the town's arrivals; without this they stay spent across a death.
  offered.clear();
  strangerEndBeat = null;
  debug("Resetting events", story.currentState, story.nextEvents());
};

export {
  onDayChange,
  onHistoryActionsChanged,
  onHistoryChanged,
  onLevelChanged,
  onResourceChanged,
  resetEvents,
  trigger,
};
